import React, { useCallback, useEffect, useState } from "react";
import { calibrationService } from "../../../services/CalibrationService";
import DismissibleMessage from "./DismissibleMessage";
import { listInitializer, Dropdown } from "./Dropdown";
import { useForm } from "react-hook-form";
import { makeStyles } from "@mui/styles";
import LoadingOverlay from "../../core/LoadingOverlay";

const CATENARY_DATASET_TYPE = "palm-analysis-catenary";

const useStyles = makeStyles((theme) => ({
    formContainer: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        flexDirection: "column",
        width: "60%",
        margin: "0 auto",
        padding: theme.spacing(3),
    },
    selectContainer: {
        display: "flex",
        justifyContent: "space-between",
        width: "100%",
        marginBottom: theme.spacing(2),
    },
    formControl: {
        margin: theme.spacing(1),
        minWidth: 120,
        maxWidth: 300,
    },
    selectBox: {
        width: "100%",
    },
    topBar: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: theme.spacing(2),
        marginTop: theme.spacing(4),
    },
    metaData: {
        marginTop: theme.spacing(1),
        textAlign: "center",
        flexGrow: 1,
        fontSize: "16px",
    },
    dropdown: {
        width: "150px",
        marginRight: theme.spacing(4),
    },
    buttonContainer: {
        display: "flex",
        justifyContent: "flex-start",
        marginTop: theme.spacing(2),
    },
    button: {
        marginLeft: theme.spacing(2),
    },
    emptyTableText: {
        textAlign: "center",
        marginTop: theme.spacing(4),
        fontSize: "20px",
        fontStyle: "italic",
    },
    loadingSpinner: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        marginTop: theme.spacing(10),
    },
    refreshButtonContainer: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        borderRadius: "50%",
    },
    refreshButton: {
        "&:hover": {
            backgroundColor: null,
        },
    },
}));

function arrayBufferToBase64(buffer) {
    let binary = "";
    const bytes = new Uint8Array(buffer);
    const len = bytes.byteLength;
    for (let i = 0; i < len; i++) {
        binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary);
}

export default function PalmCatenaryViewer() {
    const classes = useStyles();
    const { setValue, watch } = useForm();
    const [clients, setClients] = useState([]);
    const [projects, setProjects] = useState([]);
    const [groups, setGroups] = useState([]);
    const [fileNames, setFileNames] = useState([]);
    const [filesListIsLoading, setFilesListIsLoading] = useState(false);
    const [catenaryDataset, setCatenaryDataset] = useState(null);
    const [loadingCatenary, setLoadingCatenary] = useState(false);
    const [error, setError] = useState(null);
    const [statusMessage, setStatusMessage] = useState(null);

    const clientValue = watch("client");
    const projectValue = watch("project");
    const fileNameValue = watch("fileName");
    const groupValue = watch("group");

    // We need to format the responses from calibrationService in order to use the select components
    const formatOptions = useCallback((options) => options.map((option) => ({ label: option, value: option })), []);

    const clearMessages = useCallback(() => {
        setStatusMessage(null);
        setError(null);
    }, []);

    // Set error message (persists until cleared by user action or navigation)
    const setErrorWithTimeout = useCallback((errorMsg) => {
        setError(errorMsg);
    }, []);

    // Set status message (persists until cleared by user action or navigation)
    const setStatusWithTimeout = useCallback((status) => {
        setStatusMessage(status);
    }, []);

    const initializeClients = listInitializer(
        calibrationService.getClients.bind(calibrationService),
        formatOptions,
        setClients,
        setErrorWithTimeout,
        "client list"
    );

    const initializeProjectNames = listInitializer(
        calibrationService.getProjectNames.bind(calibrationService),
        formatOptions,
        setProjects,
        setErrorWithTimeout,
        "project list"
    );

    const initializeFileNames = listInitializer(
        calibrationService.getAllDttmsFromClientProject.bind(calibrationService),
        formatOptions,
        setFileNames,
        setErrorWithTimeout,
        "file list",
        setFilesListIsLoading
    );

    const initializeGroups = listInitializer(
        calibrationService.getAllGroupsFromDttm.bind(calibrationService),
        formatOptions,
        setGroups,
        setErrorWithTimeout,
        "group list"
    );

    const getCatenaryDataset = useCallback(async () => {
        try {
            setLoadingCatenary(true);
            const response = await calibrationService.getExactDataset(
                clientValue,
                projectValue,
                groupValue,
                CATENARY_DATASET_TYPE,
                fileNameValue,
                true
            );

            const base64data = "data:image/jpeg;base64," + arrayBufferToBase64(response);
            setCatenaryDataset(base64data);
        } catch (error) {
            console.error(`Failed to get catenary dataset, error: ${error}`);
            setErrorWithTimeout(`Failed to get catenary dataset, error: ${error}`);
        } finally {
            setLoadingCatenary(false);
        }
    }, [clientValue, projectValue, groupValue, fileNameValue, catenaryDataset, setErrorWithTimeout, setStatusWithTimeout]);

    useEffect(() => {
        clearMessages();
        initializeClients();
    }, []);

    useEffect(() => {
        clearMessages();
        setProjects([]);
        setFileNames([]);
        setGroups([]);
        setCatenaryDataset(null);
        setValue("project", null);
        setValue("fileName", null);
        setValue("group", null);
        if (clientValue) {
            initializeProjectNames(clientValue);
        }
    }, [clientValue]);

    useEffect(() => {
        clearMessages();
        setFileNames([]);
        setGroups([]);
        setCatenaryDataset(null);
        setValue("fileName", null);
        setValue("group", null);
        if (clientValue && projectValue) {
            initializeFileNames(clientValue, projectValue, CATENARY_DATASET_TYPE);
        }
    }, [projectValue]);

    useEffect(() => {
        clearMessages();
        setGroups([]);
        setCatenaryDataset(null);
        setValue("group", null);
        if (clientValue && projectValue && fileNameValue) {
            initializeGroups(clientValue, projectValue, CATENARY_DATASET_TYPE, fileNameValue);
        }
    }, [fileNameValue]);

    useEffect(() => {
        clearMessages();
        setCatenaryDataset(null);
        if (clientValue && projectValue && groupValue && fileNameValue) {
            getCatenaryDataset();
        }
    }, [groupValue]);

    return (
        <div id="form-container" className={classes.formContainer}>
            <h1 style={{ textAlign: "center" }}>Palm Catenary Viewer</h1>
            <form className={classes.selectContainer}>
                <Dropdown
                    dropdownName="Client"
                    dropdownValue={clientValue}
                    setValue={setValue}
                    options={clients}
                    disabledCondition={false}
                    refreshFunction={initializeClients}
                />
                <Dropdown
                    dropdownName="Project"
                    dropdownValue={projectValue}
                    setValue={setValue}
                    options={projects}
                    disabledCondition={!clientValue}
                    refreshFunction={() => initializeProjectNames(clientValue)}
                />
                <Dropdown
                    dropdownName="File"
                    dropdownValue={fileNameValue}
                    setValue={setValue}
                    setValueName="fileName"
                    options={fileNames}
                    formRegisterName="fileName"
                    disabledCondition={!projectValue || !fileNames.length}
                    refreshFunction={() => initializeFileNames(clientValue, projectValue, CATENARY_DATASET_TYPE)}
                    sortFunction={(a, b) => new Date(b.value) - new Date(a.value)}
                />
                <Dropdown
                    dropdownName="Group"
                    dropdownValue={groupValue}
                    setValue={setValue}
                    options={groups}
                    disabledCondition={!fileNameValue || !groups.length}
                    refreshFunction={() =>
                        initializeGroups(clientValue, projectValue, CATENARY_DATASET_TYPE, fileNameValue)
                    }
                    sortFunction={(a, b) => a.value.localeCompare(b.value, undefined, { numeric: true })}
                />
            </form>

            <DismissibleMessage message={error} type="error" onDismiss={() => setError(null)} />
            <DismissibleMessage message={statusMessage} type="success" onDismiss={() => setStatusMessage(null)} />
            <LoadingOverlay isLoading={loadingCatenary} message="Loading in catenary figure..." />
            {!loadingCatenary && clientValue && projectValue && fileNameValue && catenaryDataset && (
                <div>
                    <img src={catenaryDataset} alt="catenary" style={{ width: "1000px", height: "125%" }} />
                </div>
            )}
        </div>
    );
}
